/**
 * Study tab — browse the 15 sheets that ground the assistant. Mirrors
 * iOS SheetsView: grouped by category, ordered by sheet number, tap to
 * open the reader. The free-form "chat with coach" experience lives
 * inside Drills selectors, not here.
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { ChevronRight, ICON_STROKE, Library, NavIcons } from "@/shared/components/icons";
import { PageContainer } from "@/shared/components/layout/PageContainer";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { listSheets } from "@/shared/api";
import { GLOSSARY } from "@/features/study/lib/glossary";
import { SHEET_CATEGORY_ORDER, type SheetMeta } from "@/shared/models";

export function Study() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["sheets"],
    queryFn: listSheets,
    staleTime: Infinity, // sheets don't change between sessions
  });

  return (
    <PageContainer>
      <PageHeader
        Icon={NavIcons.study}
        title="Study"
        subtitle="The 15 sheets the coach reads from. GTO foundations, opponent archetypes, plus a preflop range reference."
      />

      <Link
        to="/glossary"
        className="flex items-center gap-4 rounded-xl border border-border bg-surface px-4 py-3.5 hover:bg-text/5 hover:border-accent/60 transition group"
      >
        <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-accent/15 text-accent shrink-0">
          <Library size={18} strokeWidth={ICON_STROKE} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-text group-hover:text-accent transition">
            Glossary
          </span>
          <span className="block text-xs text-text-muted mt-0.5">
            Plain-English definitions of {GLOSSARY.length} poker terms the coach
            uses: abbreviations, bet types, math, and more.
          </span>
        </span>
        <ChevronRight
          size={14}
          strokeWidth={ICON_STROKE}
          className="text-text-muted/60 group-hover:text-accent group-hover:translate-x-0.5 transition shrink-0"
        />
      </Link>

      {isLoading && <div className="text-sm text-text-muted">Loading…</div>}
      {error && (
        <div className="text-sm text-danger">
          Couldn't load sheets:{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </div>
      )}

      {data && <SheetGroups sheets={data} />}
    </PageContainer>
  );
}

function SheetGroups({ sheets }: { sheets: SheetMeta[] }) {
  // Group preserving canonical category order; trailing groups in disk
  // order for any category we don't recognize.
  const byCategory = new Map<string, SheetMeta[]>();
  for (const s of sheets) {
    const arr = byCategory.get(s.category) ?? [];
    arr.push(s);
    byCategory.set(s.category, arr);
  }
  const ordered: string[] = [];
  for (const c of SHEET_CATEGORY_ORDER) if (byCategory.has(c)) ordered.push(c);
  for (const c of byCategory.keys()) if (!ordered.includes(c)) ordered.push(c);

  return (
    <div className="space-y-6">
      {ordered.map((category) => {
        const inCat = byCategory.get(category)!;
        return (
          <section key={category}>
            <h2 className="text-xs uppercase tracking-wider text-text-muted mb-2">
              {category}
            </h2>
            <ul className="rounded-xl border border-border bg-surface overflow-hidden divide-y divide-border/50">
              {inCat
                .slice()
                .sort((a, b) => a.number - b.number)
                .map((sheet) => (
                  <li key={sheet.id}>
                    <Link
                      to={`/study/${sheet.id}`}
                      className="flex items-center gap-4 px-4 py-3 hover:bg-text/5 transition group"
                    >
                      <span className="font-mono text-xs text-text-muted/70 tabular-nums w-7 text-right">
                        {sheet.number.toString().padStart(2, "0")}
                      </span>
                      <span className="text-sm text-text group-hover:text-accent transition flex-1">
                        {sheet.displayName}
                      </span>
                      <ChevronRight
                        size={14}
                        strokeWidth={ICON_STROKE}
                        className="text-text-muted/60 group-hover:text-accent group-hover:translate-x-0.5 transition"
                      />
                    </Link>
                  </li>
                ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
